import React, { useEffect, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useRoomLifeCycleStateQuery } from '../../../../../../queries/roomLifeCycleState';
import { useAcceptanceViewFlatsSyncUpdate } from '../../../../api/acceptanceViewFlats';
import { useFormController } from '../../../../../../helpers/formManager';
import { getChangedLifeCycleData } from '../../utils/getChangedLifeCycleData';
import { createLifeCycleUpdater } from '../../utils/createLifeCycleUpdater';
import {
  useModalMethods,
  useModalWatch,
} from '../../../../shared/packages/modalManager';
import FetchLifeCycleOptionsErrorAlert from '../FetchLifeCycleOptionsErrorAlert';
import {
  addressIdFieldName,
  lifeCycleEntitiesMap,
  selectFieldNames,
} from '../../constants';
import { roomStageIdsMap } from '../../../../../../constants/room/lifeCycle';
import Body from './Body';
import { PropsProvider } from '../../services/propsProvider';
import styles from './index.module.scss';

const labels = {
  [lifeCycleEntitiesMap.stage]: 'стадии',
  [lifeCycleEntitiesMap.step]: 'этапа',
  [lifeCycleEntitiesMap.acceptanceStatus]: 'результата приемки',
};

const BaseModal = ({
  name,
  getInitialValue,
  getOnSubmit,
  entity,
  confirmLoading,
  children,
}) => {
  const [changedLifeCycleData, setChangedLifeCycleData] = useState(null);

  const [isFetchOptionsError, setIsFetchOptionsError] = useState(false);

  const { isOpen, props: { room } = {} } = useModalWatch({ name });

  const { closeAndKeepProps, removeProps } = useModalMethods();

  const syncUpdateFlatsView = useAcceptanceViewFlatsSyncUpdate();

  const {
    data: lifeCycleState,
    isFetching,
    refetch,
    remove,
  } = useRoomLifeCycleStateQuery({
    params: { addressId: room?.id },
  });

  const canChange = !isFetching && checkCanChange(lifeCycleState, room);

  useEffect(() => {
    if (typeof lifeCycleState === 'object' && room != null) {
      const changedData = getChangedLifeCycleData(room, lifeCycleState);

      if (changedData) {
        const updater = createLifeCycleUpdater(room.id, lifeCycleState);
        syncUpdateFlatsView(updater);

        if (canChange) {
          setChangedLifeCycleData(changedData);
        }
      }
    }
  }, [room, lifeCycleState, syncUpdateFlatsView, canChange]);

  const initialValue = getInitialValue(room, lifeCycleState);

  const values = {
    [selectFieldNames[entity]]: initialValue,
    [addressIdFieldName]: room?.id,
  };

  const { handleSubmit, control, reset } = useFormController({
    mode: 'onChange',
    values,
  });

  const closeModal = () => {
    closeAndKeepProps(name);
  };

  const afterClose = () => {
    if (changedLifeCycleData) {
      setChangedLifeCycleData(null);
    }

    if (isFetchOptionsError) {
      setIsFetchOptionsError(false);
    }

    reset();
    removeProps(name);
    remove();
  };

  const handleOk = () => {
    switch (true) {
      case Boolean(changedLifeCycleData): {
        setChangedLifeCycleData(null);
        break;
      }

      case canChange: {
        const onSubmit = getOnSubmit({ closeModal });
        const submit = handleSubmit(onSubmit);
        submit();
        break;
      }

      default: {
        closeModal();
      }
    }
  };

  return (
    <Modal
      title={`Изменение ${labels[entity]} - ${room?.typeName} ${room?.flat}`}
      visible={isOpen}
      destroyOnClose
      onOk={handleOk}
      onCancel={closeModal}
      okButtonProps={{
        disabled: isFetching || isFetchOptionsError || !canChange,
      }}
      centered
      confirmLoading={confirmLoading}
      afterClose={afterClose}
    >
      <PropsProvider
        name={name}
        entity={entity}
        control={control}
        initialValue={initialValue}
        setIsFetchOptionsError={setIsFetchOptionsError}
      >
        <Spin spinning={isFetching}>
          <div className={styles.body}>
            {lifeCycleState === 'Error' && (
              <FetchLifeCycleOptionsErrorAlert
                refetch={refetch}
                entity={entity}
                canChange={canChange}
              />
            )}

            {!isFetching && (
              <Body
                canChange={canChange}
                changedLifeCycleData={changedLifeCycleData}
              >
                {children}
              </Body>
            )}
          </div>
        </Spin>
      </PropsProvider>
    </Modal>
  );
};

export default BaseModal;

function checkCanChange(lifeCycleState, room) {
  const stageId = lifeCycleState?.stage?.id ?? room?.stageAddressLifeCycle?.id;
  return stageId === roomStageIdsMap.acceptance;
}
